/**
 * LibraryStep — create-wizard step 8 (W12 Part B, Task B5).
 *
 * Optional block-library examples, as the wizard's selection table; the ⓘ of a
 * row swaps the example details INTO the step (never a second dialog —
 * designer invariant 18). The whitelist×library contradiction (SPEC-019 /
 * AC-P3-039) is named here because it is the step that can fix it, and the
 * submit refuses it anyway.
 */
import React from 'react';
import { Alert, Stack } from '@mui/material';
import type { BlockLibraryExampleDoc, BlockLibrarySelection } from '@modules/shared-types';
import { BuildingBlocksSection } from '../../BuildingBlocksSection';

export interface LibraryAvailabilityConflict {
  exampleId: string;
  name: string;
  blockType: string;
}

export interface LibraryStepProps {
  projectExamples: BlockLibraryExampleDoc[];
  systemExamples: BlockLibraryExampleDoc[];
  strategyVariables: Record<string, unknown>;
  value: BlockLibrarySelection[];
  onChange: (selections: BlockLibrarySelection[]) => void;
  /** Materialized selections whose block type the step-7 whitelist excludes. */
  conflicts: LibraryAvailabilityConflict[];
  strategyName?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
}

export const LibraryStep: React.FC<LibraryStepProps> = ({
  projectExamples,
  systemExamples,
  strategyVariables,
  value,
  onChange,
  conflicts,
  strategyName,
  disabled = false,
  loading = false,
  error = null,
}) => (
  <Stack spacing={2}>
    <BuildingBlocksSection
      variant="table"
      projectExamples={projectExamples}
      systemExamples={systemExamples}
      strategyVariables={strategyVariables}
      value={value}
      onChange={onChange}
      strategyName={strategyName}
      disabled={disabled}
      loading={loading}
      error={error}
    />
    {conflicts.length > 0 && (
      <Alert severity="error" data-testid="strategy-library-availability-conflict">
        {conflicts.map((conflict) => (
          <div key={conflict.exampleId}>
            «{conflict.name}» is a <b>{conflict.blockType}</b> block, which «Block types»
            excludes — remove the example or allow {conflict.blockType}.
          </div>
        ))}
      </Alert>
    )}
  </Stack>
);

LibraryStep.displayName = 'LibraryStep';
